import { useEffect, useRef, useState } from "react";
import { PhotoMasonry } from "@/components/photo-grid/PhotoMasonry";
import { Bookmark, CheckCircle2, Clock3, Star } from "lucide-react";
import { usePublicGalleryVersionURLs, type PublicGalleryScope } from "@/features/gallery/api";
import type { PublicGalleryAsset } from "@/features/gallery/types";
import type { SelectionStage } from "./CustomerGalleryControls";

// Lưới ảnh của khách — trình bày lại theo dự án PHOTO (CEO chốt 27/08/2026).
// (Tên tệp giữ nguyên "CustomerMasonry" để không đổi cấu trúc dự án; bên trong không còn masonry.)
//
// Ba thay đổi so với bản cũ, cả ba đều là bài học PHOTO:
//  1. Ô ĐỀU NHAU dọc 2:3, khe 2px, KHÔNG bo góc — nhìn như trang tạp chí, ảnh là nhân vật chính.
//     Không hiện tên file dưới ô (muốn biết thì mở ảnh lớn).
//  2. KHÔNG còn khung cuộn riêng (trước là h-[65dvh] overflow-auto lồng trong trang → 2 thanh
//     cuộn). Lưới nằm trong dòng chảy của trang; cuộn cả trang một mạch, gần cuối tự hiện thêm
//     qua sentinel + IntersectionObserver (rootMargin 600px — nạp trước một màn hình).
//     Album lớn không mount hết một lượt: chỉ hiện BATCH ảnh đầu, chạm sentinel thì hiện thêm
//     BATCH nữa; hết phần đã có mới xin trang sau từ máy chủ (onNearEnd). Nhờ vậy album 3.000
//     ảnh vẫn chỉ mount vài chục ô lúc mở trang, và ảnh `loading="lazy"` chỉ tải khi sắp lọt màn.
//  3. MỖI Ô MỘT TRÁI TIM (góc trên-trái, bấm được) thay cho 2 nút chữ "Nháp/Cuối". Tim ghi vào
//     bước đang chọn (dải Nháp → Cuối trên đầu trang quyết định). Huy hiệu góc trên-phải chỉ để
//     NHÌN (pointer-events-none): ⭐ đề xuất, ✓ đã vào danh sách cuối, dấu nháp khi đang ở bước
//     cuối — không bấm được, đúng luật "huy hiệu không phải nút" của PHOTO.

/** Màu trái tim — token `--heart` của PHOTO (#431613, nâu trầm). */
const HEART = "#431613";
/** Số ô hiện thêm mỗi lần chạm sentinel — đủ vài màn hình, vẫn giữ DOM nhỏ lúc mở trang. */
const BATCH = 60;

export function CustomerMasonry({
  scope,
  items,
  draftSelectedIds,
  finalSelectedIds,
  stage,
  canSelect,
  locked,
  onOpen,
  onToggle,
  onNearEnd,
  loadingMore,
  hasMore,
}: {
  scope: PublicGalleryScope;
  items: PublicGalleryAsset[];
  draftSelectedIds: Set<string>;
  finalSelectedIds: Set<string>;
  stage: SelectionStage;
  canSelect: boolean;
  locked: boolean;
  onOpen: (asset: PublicGalleryAsset) => void;
  /** Tim ghi vào bước đang chọn — trang quyết định gọi mutation nháp hay cuối. */
  onToggle: (asset: PublicGalleryAsset, selected: boolean) => void;
  onNearEnd?: () => void;
  loadingMore?: boolean;
  hasMore?: boolean;
}) {
  const sentinel = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(BATCH);
  // Sentinel chạm màn: còn ảnh chưa hiện → hiện thêm BATCH; hết rồi → xin trang sau từ máy chủ.
  // Giữ callback trong ref để observer không phải dựng lại theo từng render.
  const nearEnd = useRef<() => void>(() => {});
  // Cập nhật ref trong effect, KHÔNG viết thẳng ở thân render: ghi ref lúc
  // render là tác dụng phụ trong pha dựng cây, React có thể chạy lại hoặc bỏ
  // dở pha đó. Effect này cố ý không có mảng phụ thuộc — chạy sau MỌI lần
  // render để callback luôn nhìn thấy state mới nhất, và nó đứng TRƯỚC effect
  // dựng observer nên lúc observer bắn thì ref đã đúng.
  useEffect(() => {
    nearEnd.current = () => {
      if (visibleCount < items.length) setVisibleCount((count) => count + BATCH);
      else onNearEnd?.();
    };
  });

  useEffect(() => {
    const node = sentinel.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) nearEnd.current();
      },
      { rootMargin: "600px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
    // Dựng lại observer mỗi khi lưới dài ra: sentinel còn trong tầm thì bắn tiếp ngay,
    // tránh kẹt ở đúng một BATCH khi màn hình rất cao.
  }, [visibleCount, items.length]);

  if (items.length === 0) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Album chưa có ảnh.</p>;
  }

  const showHeart = canSelect && !locked;
  const shownItems = items.slice(0, visibleCount);
  const allShown = visibleCount >= items.length;

  return (
    <div data-testid="customer-masonry">
      {/*
        Điện thoại 2 CỘT (CEO chốt 27/08/2026). Trước là 3 cột: ở 375px mỗi tấm chỉ ~110px,
        khách U40-50 ngồi lọc 500 ảnh không nhìn ra mặt ai. Đổi lại phải cuộn dài hơn — CEO
        chấp nhận. Từ md trở lên giữ nguyên 4/5 cột vì màn rộng không gặp vấn đề này.
      */}
      {/* Lưới dùng chung với mười màn kia. Trước 26/09/2026 màn này ép mọi
          tấm về khổ 2:3 — gần chuẩn nhất trong 11 màn, nhưng vẫn là CẮT.

          Cỡ ô 168px giữ đúng mật độ CEO chốt 27/08/2026 cho điện thoại: ở
          375px ra 2 cột (~186px mỗi tấm). Trước đó là 3 cột ~110px và khách
          U40-50 ngồi lọc 500 ảnh không nhìn ra mặt ai. */}
      <PhotoMasonry
        label="Album ảnh"
        targetWidth={168}
        tiles={shownItems.map((item) => ({
          id: item.assetId,
          url: "",
          // `minimum: 0` bên hợp đồng nghĩa là 0 CÓ THỂ tới — và 0 ở đây là
          // CHƯA ĐO, không phải ảnh rộng 0px. Đổi về null để lưới lùi về cách
          // cũ thay vì chia cho 0.
          width: item.version.width > 0 ? item.version.width : null,
          height: item.version.height > 0 ? item.version.height : null,
          flags: [],
          caption: item.originalFilename,
          label: `Mở ${item.originalFilename}`,
          overlay: (
            <CustomerTileOverlay
              item={item}
              picked={
                stage === "final"
                  ? finalSelectedIds.has(item.assetId)
                  : draftSelectedIds.has(item.assetId)
              }
              draftSelected={draftSelectedIds.has(item.assetId)}
              finalSelected={finalSelectedIds.has(item.assetId)}
              stage={stage}
              showHeart={showHeart}
              onToggle={onToggle}
            />
          ),
        }))}
        onOpen={(t) => {
          const item = shownItems.find((x) => x.assetId === t.id);
          if (item) onOpen(item);
        }}
        renderImage={(t) => {
          const item = shownItems.find((x) => x.assetId === t.id);
          if (!item) return null;
          // Danh sách album nay trả sẵn URL đã ký cho từng ảnh (fix
          // `fab912c8`): dùng luôn thì 3.000 ảnh không đẻ ra 3.000 lời gọi
          // phụ. Chỉ ảnh nào thiếu mới hỏi riêng — và nhánh hỏi-riêng nằm ở
          // component TileImageRemote, để hook không bị mount rồi tắt bằng
          // `enabled: false` (mount-rồi-tắt vẫn dựng 3.000 observer thừa).
          return item.urls?.medium ? (
            <TileImage src={item.urls.medium} alt="" />
          ) : (
            <TileImageRemote scope={scope} item={item} />
          );
        }}
        emptyText="Album chưa có ảnh."
      />
      <div ref={sentinel} className="flex justify-center py-4 text-sm text-muted-foreground">
        {loadingMore || !allShown ? "Đang tải thêm ảnh…" : hasMore ? null : "Đã hết ảnh"}
      </div>
    </div>
  );
}

/**
 * Lớp phủ riêng của màn khách: nút tim + ba huy hiệu.
 *
 * Giữ nguyên chữ nghĩa cũ thay vì đổi sang huy hiệu dùng chung: ba nhãn ở đây
 * ("Photo đề xuất", "Đã vào danh sách cuối", "Có trong nháp") nói ba chuyện
 * RIÊNG của luồng chọn ảnh hai vòng, không trùng nghĩa với bộ huy hiệu chung.
 * Ép chúng vào bộ chung là đổi nghĩa trước mắt khách đang chọn ảnh.
 */
function CustomerTileOverlay({
  item,
  picked,
  draftSelected,
  finalSelected,
  stage,
  showHeart,
  onToggle,
}: {
  item: PublicGalleryAsset;
  picked: boolean;
  draftSelected: boolean;
  finalSelected: boolean;
  stage: SelectionStage;
  showHeart: boolean;
  onToggle: (asset: PublicGalleryAsset, selected: boolean) => void;
}) {
  return (
    <>
      {showHeart ? (
        <button
          type="button"
          aria-pressed={picked}
          aria-label={`${picked ? "Bỏ chọn" : "Chọn"} ${item.originalFilename}`}
          onClick={() => onToggle(item, !picked)}
          className="absolute left-1 top-1 flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-xl leading-none shadow-sm transition-transform active:scale-90"
        >
          <span aria-hidden style={{ color: picked ? HEART : "#52525B" }}>
            {picked ? "♥" : "♡"}
          </span>
        </button>
      ) : null}

      {/* Huy hiệu chỉ để nhìn — không bấm được (luật PHOTO). */}
      <span className="pointer-events-none absolute right-1 top-1 flex items-center gap-1">
        {item.recommended ? (
          <span
            role="img"
            aria-label="Photo đề xuất"
            title="Photo đề xuất"
            className="inline-flex items-center rounded bg-white/90 px-1 py-0.5 shadow-sm"
          >
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-600" />
          </span>
        ) : null}
        {stage !== "final" && finalSelected ? (
          <span
            role="img"
            aria-label="Đã vào danh sách chốt"
            title="Đã vào danh sách chốt"
            className="inline-flex items-center rounded bg-primary px-1 py-0.5 text-primary-foreground shadow-sm"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
          </span>
        ) : null}
        {stage === "final" && draftSelected ? (
          <span
            role="img"
            aria-label="Có trong nháp"
            title="Có trong nháp"
            className="inline-flex items-center rounded bg-white/90 px-1 py-0.5 text-amber-700 shadow-sm"
          >
            <Bookmark className="h-3.5 w-3.5 fill-amber-400" />
          </span>
        ) : null}
      </span>
    </>
  );
}

/** Ô ảnh thuần trình bày — chưa có đường dẫn thì hiện đồng hồ chờ. */
function TileImage({ src, alt }: { src: string | undefined; alt: string }) {
  if (!src) {
    return (
      <span className="grid h-full place-items-center text-xs text-muted-foreground">
        <Clock3 className="h-4 w-4" />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      className="h-full w-full object-cover"
    />
  );
}

/** Chỉ mount cho ảnh KHÔNG kèm sẵn URL đã ký — mới phải hỏi máy chủ ký riêng tấm đó. */
function TileImageRemote({ scope, item }: { scope: PublicGalleryScope; item: PublicGalleryAsset }) {
  const urls = usePublicGalleryVersionURLs(
    scope,
    item.assetId,
    item.version.id,
    item.version.status === "ready",
  );
  return <TileImage src={urls.data?.medium} alt={item.originalFilename} />;
}
